import { useEffect, useState } from "react";
import { Table, Button, Select, Modal, Row, message, Typography } from "antd";
import type { TableColumnsType, TableProps } from "antd";
import ManualFuelVolumeModal from "./ManualFuelVolumeModal";
import {
  ResponseAPI,
  OptionFilterItem,
  FilterCombustion,
  CombustionManualFuelVolume,
} from "src/entity/manual-fuel-volume/manual-fuel-volume.entity";
import {
  getManualFuelVolumeCombustions,
  updateManualFuelVolumeCombust,
  getManFuelVolumeOptionFilterCombust,
  calculateReportVocEmission,
} from "src/services/combustion.service";
import { profileStore } from "src/stores/profile-store";
import { useShallow } from "zustand/react/shallow";
import { canEditTable, canViewTable } from "../../PermissionCheck/PermissionCheck";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { BsPencilSquare } from "react-icons/bs";
import { componentPalette } from "src/theme/component-palette";
import { CommonError, CommonSuccess } from "src/entity/common/menus-enum.enum";
import { parseAndFormatScientificNumber } from "src/utils/process-number";
import { generateYearArray } from "src/utils/array";
import { addCenterAlign } from "src/components/AntdCustomPart/TableTitle.tsx/TableTitle";

const { Text } = Typography;

const ManualFuelVolumeTable = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const currentYear = new Date().getFullYear();
  const [companyId, setCompanyId] = useState<string>("");
  const profile = profileStore(useShallow((state) => state.profile));
  const { permissions } = useNavigationMenu();
  const [data, setData] = useState<CombustionManualFuelVolume[]>();
  const [optionYears, setOptionYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [filterStackId, setFilterStackId] = useState<number[]>();
  const [filterSourceId, setFilterSourceId] = useState<number[]>();
  const [filterArea, setFilterArea] = useState<string[]>();
  const [filterFuelName, setFilterFuelName] = useState<string[]>();
  const [filterCombustion, setFilterCombustion] = useState<FilterCombustion>({
    stackId: null,
    sourceId: null,
    area: null,
    fuelName: null,
  });
  const [initialData, setInitialData] = useState<CombustionManualFuelVolume>({
    year: "",
    companyId: "",
    stackId: "",
    sourceId: "",
    area: "",
    fuelName: "",
    fuelType: "",
    manualFuelVolume: {
      unit: "",
      jan: {
        value: 0,
        reportStatus: "",
      },
      feb: {
        value: 0,
        reportStatus: "",
      },
      mar: {
        value: 0,
        reportStatus: "",
      },
      apr: {
        value: 0,
        reportStatus: "",
      },
      may: {
        value: 0,
        reportStatus: "",
      },
      jun: {
        value: 0,
        reportStatus: "",
      },
      jul: {
        value: 0,
        reportStatus: "",
      },
      aug: {
        value: 0,
        reportStatus: "",
      },
      sep: {
        value: 0,
        reportStatus: "",
      },
      oct: {
        value: 0,
        reportStatus: "",
      },
      nov: {
        value: 0,
        reportStatus: "",
      },
      dec: {
        value: 0,
        reportStatus: "",
      },
    },
  });

  const fetchYear = () => {
    const years = generateYearArray();
    setOptionYears(years as number[]);
    setSelectedYear(years[0] as number);
  };

  const createParamFilter = () => {
    let queryParam = "";
    let offset = 0;
    let limit = pageSize;
    if (selectedYear) {
      queryParam += `year=${selectedYear}`;
    }
    if (current) {
      let resultCalOffset = calPagination(current);
      if (resultCalOffset) {
        offset = resultCalOffset;
      }
      queryParam += `&offset=${offset}`;
    }
    if (limit) {
      queryParam += `&limit=${limit}`;
    }
    if (filterCombustion) {
      const str = Object.entries(filterCombustion)
        .filter(([_, value]) => value != null && value.length > 0)
        .map(([key, value]) => `&${key}=[${[value.join(",")]}]`)
        .join("");
      queryParam += str;
    }

    return queryParam;
  };

  const fetchCombustions = async () => {
    try {
      if (companyId) {
        setLoading(true);
        let offset = 0;
        let resultCalOffset = calPagination(current);
        if (resultCalOffset) {
          offset = resultCalOffset;
        }
        const queryParam = createParamFilter();
        let response = await getManualFuelVolumeCombustions(companyId, queryParam);
        let responseData: ResponseAPI = response.data;
        if (responseData.code === 200) {
          setLoading(false);
          let combustions = responseData.data.combustions.map((el, index) => {
            return {
              no: (index + offset + 1).toString(),
              ...el,
            };
          });
          setData([...combustions]);
          setTotal(responseData.data.total);
        }
      }
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const fetchOptionFilterCombustions = async () => {
    try {
      if (companyId) {
        let query = `year=${selectedYear}`;
        let response = await getManFuelVolumeOptionFilterCombust(companyId, query);
        let responseData: ResponseAPI = response.data;
        if (responseData.code === 200) {
          const filter = response.data.data.filter;
          if (filter.length > 0) {
            setFilterStackId(filter.find((item: OptionFilterItem) => item.key === "stackId")?.filter ?? []);
            setFilterSourceId(filter.find((item: OptionFilterItem) => item.key === "sourceId")?.filter ?? []);
            setFilterArea(filter.find((item: OptionFilterItem) => item.key === "area")?.filter ?? []);
            setFilterFuelName(filter.find((item: OptionFilterItem) => item.key === "fuelName")?.filter ?? []);
          } else {
            setFilterStackId([]);
            setFilterSourceId([]);
            setFilterArea([]);
            setFilterFuelName([]);
          }
        } else {
          messageApi.open({
            type: "error",
            content: CommonError.SOMETHING_WRONG,
          });
        }
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  const handleYearChange = (value: number) => {
    setSelectedYear(value);
  };

  const handleSaveCombustion = async (data: CombustionManualFuelVolume) => {
    try {
      let id = data._id;

      if (id && companyId) {
        setLoading(true);
        data.companyId = companyId;
        let response = await updateManualFuelVolumeCombust(companyId, id, data);
        const responseData: ResponseAPI = response.data;

        if (responseData.code === 200) {
          messageApi.open({
            type: "success",
            content: CommonSuccess.SAVED_SUCCESSFULL,
          });
          processCalculateReport(id);
          fetchCombustions();
          setLoading(false);
          setShowModal(false);
        } else {
          messageApi.open({
            type: "error",
            content: CommonError.SOMETHING_WRONG,
          });
        }
      }
    } catch (error) {
      setLoading(false);
      messageApi.open({
        type: "error",
        content: CommonError.SOMETHING_WRONG,
      });
    } finally {
      setLoading(false);
    }
  };

  const processCalculateReport = async (id: string) => {
    try {
      setLoading(true);
      await calculateReportVocEmission(id);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModalManCombust = (combustion: CombustionManualFuelVolume) => {
    setInitialData(combustion);
    setShowModal(true);
  };

  const resetFilter = () => {
    setFilterCombustion({});
  };

  const intitalData = () => {
    if (profile?.activeCompany?._id) {
      setCompanyId(profile.activeCompany._id);
    }
  };

  useEffect(() => {
    intitalData();
    fetchYear();
  }, []);

  useEffect(() => {
    intitalData();
  }, [profile?.activeCompany]);

  useEffect(() => {
    setSelectedYear(currentYear);
    setCurrent(1);
  }, [companyId]);

  useEffect(() => {
    resetFilter();
    setCurrent(1);
  }, [selectedYear]);

  useEffect(() => {
    if (selectedYear) {
      const fetchData = async () => {
        await fetchCombustions();
        await fetchOptionFilterCombustions();
      };
      fetchData();
    }
  }, [current, filterCombustion]);

  const months = [
    { title: "Jan", dataIndex: "jan" },
    { title: "Feb", dataIndex: "feb" },
    { title: "Mar", dataIndex: "mar" },
    { title: "Apr", dataIndex: "apr" },
    { title: "May", dataIndex: "may" },
    { title: "Jun", dataIndex: "jun" },
    { title: "Jul", dataIndex: "jul" },
    { title: "Aug", dataIndex: "aug" },
    { title: "Sep", dataIndex: "sep" },
    { title: "Oct", dataIndex: "oct" },
    { title: "Nov", dataIndex: "nov" },
    { title: "Dec", dataIndex: "dec" },
    { title: "Total", dataIndex: "total" },
  ];

  const monthColumns: TableColumnsType<CombustionManualFuelVolume> = months.map((month) => ({
    title: month.title,
    dataIndex: "manualFuelVolume",
    render: (value: any) => {
      if (value && typeof value === "object") {
        const key = month.dataIndex;
        if (key === "total") {
          let total = 0;
          months.forEach((itemMonth) => {
            const monthName = itemMonth.dataIndex;
            if (value && typeof value === "object") {
            }
            if (value[monthName] && value[monthName].value) {
              total += parseFloat(value[monthName].value);
            }
          });
          return parseAndFormatScientificNumber(`${total}`, 2);
        } else {
          return parseAndFormatScientificNumber(value[key].value, 2) ?? 0;
        }
      }
    },
  }));

  const staticColumns: TableColumnsType<CombustionManualFuelVolume> = [
    {
      title: "No",
      dataIndex: "no",
      width: 50,
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Stack ID",
      dataIndex: "stackId",
      width: 100,
      filteredValue: filterCombustion?.stackId || null,
      filters: filterStackId?.map((el: number) => ({ text: el, value: el })),
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Source ID",
      dataIndex: "sourceId",
      width: 120,
      filteredValue: filterCombustion?.sourceId || null,
      filters: filterSourceId?.map((el: number) => ({ text: el, value: el })),
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Area",
      dataIndex: "area",
      width: 70,
      filteredValue: filterCombustion?.area || null,
      filters: filterArea?.map((el: string) => ({ text: el, value: el })),
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Fuel Name",
      dataIndex: "fuelName",
      width: 120,
      filteredValue: filterCombustion?.fuelName || null,
      filters: filterFuelName?.map((el: string) => ({ text: el, value: el })),
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Unit",
      dataIndex: "manualFuelVolume",

      render: (value) => {
        return value ? <Text>{value.unit}</Text> : "";
      },
    },
  ];

  const actionColumns: TableColumnsType<CombustionManualFuelVolume> = [
    {
      title: "Action",
      dataIndex: "",
      key: "action",
      render: (_, record) => (
        <div>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            disabled={!canEditTable(permissions)}
            onClick={() => {
              handleOpenModalManCombust(record);
            }}
          >
            <BsPencilSquare size={18} color={componentPalette.blueGrey} />
          </Button>
        </div>
      ),
    },
  ];

  const columns: TableColumnsType<CombustionManualFuelVolume> = addCenterAlign([...staticColumns, ...monthColumns, ...actionColumns]);

  const onTableChange: TableProps<CombustionManualFuelVolume>["onChange"] = (pagination: any, filters: any) => {
    setFilterCombustion(filters);
    setCurrent(pagination.current);
    setPageSize(pagination.pageSize);
  };

  return (
    <>
      {contextHolder}
      {showModal && (
        <ManualFuelVolumeModal
          open={showModal}
          initialData={initialData}
          loading={loading}
          onCancelManualFuelVolume={() => {
            setShowModal(false);
          }}
          onSaveManFeulVol={(data) => {
            handleSaveCombustion(data);
          }}
        />
      )}

      <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: "10px" }}>
        <div>
          <Select
            value={selectedYear}
            style={{ width: 120 }}
            onChange={handleYearChange}
            options={optionYears.map((year) => ({ label: year, value: year }))}
            loading={loading}
            size="middle"
          />
        </div>
      </div>
      <Row className="w-full">
        <Table
          className="w-full"
          style={{ whiteSpace: "nowrap" }}
          columns={columns}
          dataSource={canViewTable(permissions) ? data : []}
          onChange={onTableChange}
          rowKey="_id"
          scroll={{ x: "max-content" }}
          loading={loading}
          pagination={{
            size: "small",
            showSizeChanger: false,
            current,
            pageSize,
            total,
          }}
        />
      </Row>
    </>
  );
};

export default ManualFuelVolumeTable;
